/**
 * Shared phone normalization and validation utilities.
 * Supports Brazilian (55) and international phone numbers in E.164 format.
 */

/**
 * Normalize a phone number to E.164 digits-only format.
 * - 11 digits without country code → assumes Brazil, adds "55"
 * - 13 digits starting with "55" → Brazil, returns as-is
 * - Starts with "+" → strips "+", returns digits
 * - 12-15 digits → international, returns as-is
 */
export const normalizePhone = (value: string): string => {
  const stripped = value.replace(/[^\d+]/g, "");
  const hasPlus = stripped.startsWith("+");
  const digits = stripped.replace(/\D/g, "");

  // Brazilian: 11 local digits (DDD + number)
  if (digits.length === 11 && !hasPlus) return "55" + digits;
  // Brazilian: already has country code
  if (digits.startsWith("55") && digits.length === 13) return digits;
  // International with explicit + prefix
  if (hasPlus && digits.length >= 7 && digits.length <= 15) return digits;
  // International without + but 12-15 digits and NOT starting with 55
  if (!digits.startsWith("55") && digits.length >= 12 && digits.length <= 15) return digits;

  return digits;
};

/**
 * Check if a phone number is valid (Brazilian or international).
 */
export const isValidPhone = (value: string): boolean => {
  const stripped = value.replace(/[^\d+]/g, "");
  const hasPlus = stripped.startsWith("+");
  const digits = stripped.replace(/\D/g, "");

  if (!digits || digits.length < 7) return false;

  // Brazilian local: 10-11 digits (with or without 9th digit)
  if (!hasPlus && (digits.length === 10 || digits.length === 11)) return true;
  // Brazilian with country code
  if (digits.startsWith("55") && (digits.length === 12 || digits.length === 13)) return true;
  // International with + prefix: 7-15 digits total
  if (hasPlus && digits.length >= 7 && digits.length <= 15) return true;
  // International without +: 12-15 digits, not starting with 55
  if (!digits.startsWith("55") && digits.length >= 12 && digits.length <= 15) return true;

  return false;
};

/**
 * Check if a normalized phone number is international (non-Brazilian).
 */
export const isInternational = (normalizedPhone: string): boolean => {
  return !normalizedPhone.startsWith("55");
};

/**
 * Get phone variants for database matching.
 * For BR numbers: returns [with55, without55]
 * For international: returns [fullNumber]
 */
export const getPhoneVariants = (value: string): string[] => {
  const normalized = normalizePhone(value);
  const variants = new Set<string>();
  variants.add(normalized);

  // For Brazilian numbers, also search without country code
  if (normalized.startsWith("55") && (normalized.length === 12 || normalized.length === 13)) {
    variants.add(normalized.slice(2));
  }

  // Also add raw trimmed input if different
  const raw = value.replace(/[^\d+]/g, "").replace(/^\+/, "");
  if (raw && !variants.has(raw)) variants.add(raw);

  return Array.from(variants);
};
